
import { Prop, Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { SchemeScope } from '@/components/mixins/schemescope';
import { Online } from '@/components/mixins/online';
import { Viewer } from '@toast-ui/vue-editor';
import _isEmpty from 'lodash/isEmpty';
import VueHtml2pdf from 'vue-html2pdf';
import { LessonPlan as LessonPlanModel } from '@/models/lessonPlan';
import CoachEditor from '@/components/coach/planning/CoachEditor.vue';
import LessonPlanContent from '@/components/coach/planning/LessonPlanContent.vue';
import MoreButton from '@/components/scheme/MoreButton.vue';
import ShareModal from '@/components/coach/planning/ShareModal.vue';

@Component({
  name: 'LessonPlan',
  components: {
    CoachEditor,
    Viewer,
    LessonPlanContent,
    MoreButton,
    ShareModal,
    VueHtml2pdf,
  },
})
export default class LessonPlan extends mixins(SchemeScope, Online) {
  private editorValue = '';
  private reminders = '';
  private loading = true;
  private pdfGenerating = false;

  private editing = false;
  private adding = false;
  private newContentBlockName = '';

  private editingTitle = false;
  private newTitle = '';
  private showShareModal = false;
  private reminderPanels = [] as Array<number>;
  private contentPanels = [] as Array<number>;

  @Prop() lessonPlanUuid!: string;

  async created() {
    this.loading = true;
    await this.initializeSchemeStore(false, false); // we need to prefetch the schemes to render skill titles
    await this.$store.dispatch('lessonPlans/getCoaches'); // Used for sharing
    await this.$store.dispatch('planning/fetchItems');
    await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.lessonPlanUuid);
    if (!_isEmpty(this.plan.headers)) {
      if (this.plan.headers[0].setup) {
        this.editorValue = this.plan.headers[0].setup;
      }
      if (this.plan.headers[0].reminders) {
        this.reminders = this.plan.headers[0].reminders;
      }
    } else {
      this.editorValue = '';
      this.reminders = '';
    }
    this.newTitle = this.plan.title;
    this.loading = false;
  }

  get plan(): LessonPlanModel {
    return this.$store.getters['lessonPlans/selectedPlan'];
  }

  get planningLink() {
    return { name: 'lesson-plans', params: { schemeSlug: this.schemeSlug } };
  }

  get readOnly() {
    return this.plan.readonly === 1;
  }

  get isSharedOwner() {
    return (
      this.plan.owner && this.plan.owner.uuid === this.$store.getters['auth/loggedInUserId'] && this.plan.shares.length
    );
  }

  async saveSetup() {
    this.loading = true;
    const editor = this.$refs.setup_editor as any;
    const markdown = editor.$refs.toastuiEditor.invoke('getMarkdown');
    this.plan.headers = [];
    this.plan.headers.push({ setup: markdown, reminders: this.reminders });

    try {
      await this.$store.dispatch('lessonPlans/updateSessionSetup', this.plan);
      this.$alert('success', this.$t('planning.session_setup_updated'));
      this.editorValue = markdown;
    } catch {
      this.$alert('error', this.$t('planning.session_setup_not_updated'));
    }
    this.loading = false;
    this.editing = false;
  }

  async createContentBlock() {
    try {
      this.loading = true;
      await this.$store.dispatch('lessonPlans/createSection', {
        planUuid: this.lessonPlanUuid,
        title: this.newContentBlockName,
      });
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.lessonPlanUuid);
      this.$alert('success', this.$t('planning.content_block_created'));
      this.newContentBlockName = '';
      this.adding = false;
    } catch {
      this.$alert('error', this.$t('planning.content_block_not_created'));
    }
    this.loading = false;
  }

  async duplicatePlan() {
    try {
      const resp = await this.$store.dispatch('lessonPlans/duplicatePlan', this.plan);
      const newUuid = resp.response.headers
        .get('Location')
        .split('/')
        .pop();
      if (newUuid) {
        await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', newUuid);
        this.$router
          .push({ name: 'lesson-plan', params: { schemeSlug: this.schemeSlug, lessonPlanUuid: newUuid } })
          .catch(() => {
            // ignore routing errors
          });
      }
      this.$alert('success', this.$t('planning.session_duplicated'));
    } catch (error) {
      this.$alert('error', this.$t('planning.session_not_duplicated'));
    }
  }

  async deletePlan() {
    const res = await this.$confirmDangerous(
      this.$t('global.are_you_sure'),
      this.$t('global.cancel'),
      this.$t('planning.delete_plan'),
      this.$t('global.cannot_be_undone'),
      this.$t('planning.confirm_deletion', { plan: this.plan.title }),
      this.plan.title
    );

    if (res) {
      try {
        await this.$store.dispatch('lessonPlans/deletePlan', this.plan.uuid);
        await this.$store.dispatch('storeDb/deleteLessonPlan', this.plan.uuid);
        this.$router.push({ name: 'planning' }).catch(() => {
          // ignore routing errors
        });
        this.$alert('success', this.$t('planning.session_deleted'));
      } catch (error) {
        this.$alert('error', this.$t('planning.session_not_deleted'));
      }
    }
  }

  renamePlan() {
    this.editingTitle = true;
  }

  async updateTitle() {
    try {
      await this.$store.dispatch('lessonPlans/updatePlanTitle', this.newTitle);
      await this.$store.dispatch('storeDb/fetchAndSaveLessonPlan', this.lessonPlanUuid); // Update plan in db
      await this.$store.dispatch('lessonPlans/fetchLessonPlanFromDb', this.plan.uuid); // Fetch updated plan
      this.editingTitle = false;
      this.$alert('success', this.$t('planning.title_updated'));
    } catch (error) {
      this.editingTitle = false;
      this.$alert('error', this.$t('planning.title_not_updated'));
    }
  }

  cancelEditTitle() {
    this.editingTitle = false;
    this.newTitle = this.plan.title;
  }

  edit() {
    if (this.readOnly) {
      this.showDuplicatePlan();
    } else {
      this.editing = true;
    }
  }

  addNewContentBlock() {
    if (this.readOnly) {
      this.showDuplicatePlan();
    } else {
      this.adding = true;
    }
  }

  async showDuplicatePlan() {
    const res = await this.$confirm(this.$t('planning.confirm_duplicate') as string, {
      title: this.$t('planning.duplicate_session') as string,
      color: 'dark',
      icon: 'mdi-help-circle-outline',
      buttonTrueText: this.$t('global.yes') as string,
      buttonFalseText: this.$t('global.no') as string,
    });
    if (res) {
      this.duplicatePlan();
    }
  }

  get sessionPlanGroupsEnabled() {
    return this.$store.getters['auth/isFeatureEnabled']('ENABLE_SESSION_PLANNING');
  }

  get canAddSession() {
    return this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN');
  }

  get canEditSession() {
    return this.canAddSession || this.$store.getters['auth/isFeatureEnabled']('CAN_EDIT_SESSION_PLAN');
  }

  generatePdf() {
    this.reminderPanels = [...Array(this.plan.sections.length).keys()];
    this.contentPanels = this.reminderPanels;
    setTimeout(() => {
      if (this.$refs.html2Pdf) {
        const ref = this.$refs.html2Pdf as any;
        ref.generatePdf();
      }
    }, 500);
  }

  async beforeDownload({ html2pdf, options, pdfContent }: { html2pdf: any; options: any; pdfContent: any }) {
    // Since we wait for the panels to open first, this creates an empty first page so we delete it
    await html2pdf()
      .set(options)
      .from(pdfContent)
      .toPdf()
      .get('pdf')
      .then((pdf: any) => {
        pdf.deletePage(1);
      })
      .save();
    this.pdfGenerating = false;
  }

  get pdfOptions() {
    const filename = this.plan.title + '-' + new Date().toISOString().slice(0, 10);
    return {
      margin: 5,
      filename,
      html2canvas: {
        background: '#ffffff',
      },
    };
  }

  get generatePdfBtnText() {
    if (this.pdfGenerating) {
      return this.$t('planning.generating');
    }
    return this.$t('planning.generate_pdf');
  }
}
